import { useEffect, useState, useCallback } from "react";
import { Link2, Unlink, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { money } from "./shared";
import { useI18n } from "../../../context/I18nContext";

export default function Conciliacion({ companyId, year, month }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!companyId) return;
    const p = new URLSearchParams({ company_id: companyId });
    if (year) p.append("year", year);
    if (month) p.append("month", month);
    api.get(`/accounting/reconciliation?${p}`).then((r) => setData(r.data)).catch(() => {});
  }, [companyId, year, month]);
  useEffect(() => { load(); }, [load]);

  const match = async (movement_id, doc_id) => {
    try { await api.post("/accounting/reconciliation/match", { movement_id, doc_id }); toast.success(t("os.contab.reconciled")); load(); } catch { toast.error(t("os.contab.err")); }
  };
  const unmatch = async (movement_id) => {
    try { await api.post("/accounting/reconciliation/unmatch", { movement_id }); load(); } catch { toast.error(t("os.contab.err")); }
  };

  if (!data) return <div className="text-navy/40">{t("os.contab.loading")}</div>;
  const s = data.stats;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="recon-stats">
        {[["movimientos", s.movements, "text-navy"], ["conciliados", s.matched, "text-emerald-600"], ["sugerencias", s.suggestions, "text-violet"], ["sinConciliar", s.unmatched, "text-destructive"]].map(([k, v, c]) => (
          <div key={k} className="bg-white border border-navy/10 rounded-lg p-5">
            <div className="overline text-navy/40 text-[0.6rem]">{t(`os.contab.reconStats.${k}`)}</div>
            <div className={`mt-2 text-2xl font-medium ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      {/* Suggestions */}
      {data.suggestions.length > 0 && (
        <div data-testid="recon-suggestions">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet" /> {t("os.contab.suggestionsTitle")}</h3>
          <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5">
            {data.suggestions.map(({ movement, doc }) => (
              <div key={movement.id} data-testid={`recon-suggestion-${movement.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-sm">
                <div className="min-w-0">
                  <div className="font-medium">{money(movement.amount)} · <span className="text-navy/50">{movement.type === "abono" ? t("os.contab.abono") : t("os.contab.cargo")}</span></div>
                  <div className="text-xs text-navy/40 truncate">{(movement.date || "").slice(0, 10)} · {movement.description || movement.counterparty}</div>
                </div>
                <div className="text-navy/30">↔</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{doc.emisor_nombre} → {doc.receptor_nombre}</div>
                  <div className="text-xs text-navy/40">{money(doc.total)} · {doc.category}</div>
                </div>
                <button data-testid={`recon-match-${movement.id}`} onClick={() => match(movement.id, doc.id)}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full bg-violet/10 text-violet px-3 py-1.5 hover:bg-violet/20 transition-colors">
                  <Link2 className="w-3.5 h-3.5" /> {t("os.contab.reconcile")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Matched */}
      {data.matched.length > 0 && (
        <div data-testid="recon-matched">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {t("os.contab.matchedTitle")}</h3>
          <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5">
            {data.matched.map(({ movement, doc }) => (
              <div key={movement.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-sm">
                <div className="min-w-0"><div className="font-medium">{money(movement.amount)}</div><div className="text-xs text-navy/40 truncate">{(movement.date || "").slice(0, 10)} · {movement.description}</div></div>
                <div className="min-w-0 flex-1 text-navy/60 truncate">{doc.emisor_nombre} · {money(doc.total)}</div>
                <button data-testid={`recon-unmatch-${movement.id}`} onClick={() => unmatch(movement.id)}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/10 text-navy/50 px-3 py-1.5 hover:text-destructive hover:border-destructive/40 transition-colors">
                  <Unlink className="w-3.5 h-3.5" /> {t("os.contab.undo")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unmatched */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-medium mb-3">{t("os.contab.unmatchedMovTitle")(data.unmatched_movements.length)}</h3>
          <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5" data-testid="recon-unmatched-movements">
            {data.unmatched_movements.length === 0 ? <div className="px-5 py-6 text-navy/40 text-sm text-center">{t("os.contab.allReconciled")}</div> :
              data.unmatched_movements.map((m) => (
                <div key={m.id} className="px-5 py-3 text-sm flex justify-between gap-2">
                  <div className="min-w-0 truncate"><span className="text-navy/40 text-xs">{(m.date || "").slice(0, 10)}</span> {m.description || m.counterparty}</div>
                  <div className={`font-medium shrink-0 ${m.type === "abono" ? "text-emerald-600" : "text-destructive"}`}>{money(m.amount)}</div>
                </div>
              ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-medium mb-3">{t("os.contab.unmatchedInvTitle")(data.unmatched_invoices.length)}</h3>
          <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5" data-testid="recon-unmatched-invoices">
            {data.unmatched_invoices.length === 0 ? <div className="px-5 py-6 text-navy/40 text-sm text-center">{t("os.contab.allReconciled")}</div> :
              data.unmatched_invoices.map((i) => (
                <div key={i.id} className="px-5 py-3 text-sm flex justify-between gap-2">
                  <div className="min-w-0 truncate"><span className="text-navy/40 text-xs">{i.category}</span> {i.emisor_nombre}</div>
                  <div className="font-medium shrink-0">{money(i.total)}</div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
